/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import {
  EnvironmentHealthcheck,
  EnvironmentHealthcheck$Outbound,
  EnvironmentHealthcheck$outboundSchema,
} from "./environmenthealthcheck.js";
import {
  EnvironmentRegion,
  EnvironmentRegion$Outbound,
  EnvironmentRegion$outboundSchema,
} from "./environmentregion.js";
import {
  EnvironmentShutdownSignal,
  EnvironmentShutdownSignal$outboundSchema,
} from "./environmentshutdownsignal.js";
import {
  EnvironmentUpstreamProtocol,
  EnvironmentUpstreamProtocol$outboundSchema,
} from "./environmentupstreamprotocol.js";

export type V1EnvironmentsUpdateSettingsRequestBody = {
  /**
   * Identifies a resource by either its unique ID or its slug.
   *
   * @remarks
   * Accepts a prefixed ID (such as 'proj_' or 'app_') or a slug.
   */
  project: string;
  /**
   * Identifies a resource by either its unique ID or its slug.
   *
   * @remarks
   * Accepts a prefixed ID (such as 'proj_' or 'app_') or a slug.
   */
  app: string;
  /**
   * Identifies a resource by either its unique ID or its slug.
   *
   * @remarks
   * Accepts a prefixed ID (such as 'proj_' or 'app_') or a slug.
   */
  environment: string;
  /**
   * Path to the Dockerfile used for builds.
   *
   * @remarks
   * Omit to leave unchanged; set null to clear and fall back to Railpack.
   */
  dockerfile?: string | null | undefined;
  /**
   * The directory your app lives in. Teral builds from here.
   *
   * @remarks
   * Use "." for the repository root, or set a subdirectory when your app
   * is nested (e.g., services/api). Omit to leave unchanged.
   */
  rootDirectory?: string | undefined;
  /**
   * Overrides the build command auto-detected by Railpack.
   *
   * @remarks
   * Omit to leave unchanged; set null to clear and fall back to auto-detection.
   */
  buildCommand?: string | null | undefined;
  /**
   * Glob patterns that trigger auto-deploys when matching files change.
   *
   * @remarks
   * Do not start a pattern with "/" or "./".
   * Use "src/**" for everything under a directory and "** /*.go" for a file type.
   * A pattern with no wildcard matches only that exact file, so "src" is not "src/**".
   * Omit to leave unchanged. Invalid patterns are rejected with a 400.
   */
  watchPaths?: Array<string> | undefined;
  /**
   * Whether pushes auto-deploy.
   *
   * @remarks
   * Omit to leave unchanged.
   */
  autoDeploy?: boolean | undefined;
  /**
   * Container port the app listens on.
   *
   * @remarks
   * Omit to leave unchanged.
   */
  port?: number | undefined;
  /**
   * CPU allocation in vCPUs. Minimum 0.25 (1/4 vCPU), in steps of 0.25.
   *
   * @remarks
   * The upper bound is your workspace's per-instance quota; exceeding it returns 400.
   * Omit to leave unchanged.
   */
  vCpus?: number | undefined;
  /**
   * Memory allocation in MiB. Minimum 256, in steps of 256.
   *
   * @remarks
   * The upper bound is your workspace's per-instance quota; exceeding it returns 400.
   * Omit to leave unchanged.
   */
  memoryMib?: number | undefined;
  /**
   * Ephemeral storage allocation in MiB, in steps of 512 (0 for none).
   *
   * @remarks
   * The upper bound is your workspace's per-instance quota; exceeding it returns 400.
   * Omit to leave unchanged.
   */
  storageMib?: number | undefined;
  /**
   * Override container entrypoint command.
   *
   * @remarks
   * Omit to leave unchanged.
   */
  command?: Array<string> | undefined;
  /**
   * HTTP healthcheck configuration.
   *
   * @remarks
   * Omit to leave unchanged; set null to remove.
   */
  healthcheck?: EnvironmentHealthcheck | null | undefined;
  /**
   * Signal sent to the container on shutdown.
   *
   * @remarks
   */
  shutdownSignal?: EnvironmentShutdownSignal | undefined;
  /**
   * Protocol used to reach the container.
   *
   * @remarks
   */
  upstreamProtocol?: EnvironmentUpstreamProtocol | undefined;
  /**
   * Path to the OpenAPI spec file within the build. Must start with a slash.
   *
   * @remarks
   * Omit to leave unchanged; set null to clear.
   */
  openapiSpecPath?: string | null | undefined;
  /**
   * Desired set of regions with per-region replica bounds.
   *
   * @remarks
   * Omit to leave regions unchanged; when present, this replaces the full set
   * (regions absent from the list are removed). At least one region is required;
   * an empty list is rejected because an environment cannot have zero regions.
   */
  regions?: Array<EnvironmentRegion> | undefined;
};

/** @internal */
export type V1EnvironmentsUpdateSettingsRequestBody$Outbound = {
  project: string;
  app: string;
  environment: string;
  dockerfile?: string | null | undefined;
  rootDirectory?: string | undefined;
  buildCommand?: string | null | undefined;
  watchPaths?: Array<string> | undefined;
  autoDeploy?: boolean | undefined;
  port?: number | undefined;
  vCpus?: number | undefined;
  memoryMib?: number | undefined;
  storageMib?: number | undefined;
  command?: Array<string> | undefined;
  healthcheck?: EnvironmentHealthcheck$Outbound | null | undefined;
  shutdownSignal?: string | undefined;
  upstreamProtocol?: string | undefined;
  openapiSpecPath?: string | null | undefined;
  regions?: Array<EnvironmentRegion$Outbound> | undefined;
};

/** @internal */
export const V1EnvironmentsUpdateSettingsRequestBody$outboundSchema: z.ZodType<
  V1EnvironmentsUpdateSettingsRequestBody$Outbound,
  z.ZodTypeDef,
  V1EnvironmentsUpdateSettingsRequestBody
> = z.object({
  project: z.string(),
  app: z.string(),
  environment: z.string(),
  dockerfile: z.nullable(z.string()).optional(),
  rootDirectory: z.string().optional(),
  buildCommand: z.nullable(z.string()).optional(),
  watchPaths: z.array(z.string()).optional(),
  autoDeploy: z.boolean().optional(),
  port: z.number().int().optional(),
  vCpus: z.number().optional(),
  memoryMib: z.number().int().optional(),
  storageMib: z.number().int().optional(),
  command: z.array(z.string()).optional(),
  healthcheck: z.nullable(EnvironmentHealthcheck$outboundSchema).optional(),
  shutdownSignal: EnvironmentShutdownSignal$outboundSchema.optional(),
  upstreamProtocol: EnvironmentUpstreamProtocol$outboundSchema.optional(),
  openapiSpecPath: z.nullable(z.string()).optional(),
  regions: z.array(EnvironmentRegion$outboundSchema).optional(),
});

export function v1EnvironmentsUpdateSettingsRequestBodyToJSON(
  v1EnvironmentsUpdateSettingsRequestBody:
    V1EnvironmentsUpdateSettingsRequestBody,
): string {
  return JSON.stringify(
    V1EnvironmentsUpdateSettingsRequestBody$outboundSchema.parse(
      v1EnvironmentsUpdateSettingsRequestBody,
    ),
  );
}
